import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { TextField, Pagination, Box, CircularProgress } from '@mui/material';
import WebsiteTable from './WebsiteTable';  // Reuse existing table component
import api from '../../api/axios';

const ReadyWebsiteList = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const searchParams = new URLSearchParams(location.search);

    const [websiteData, setWebsiteData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page')) || 1);
    const [sortField, setSortField] = useState(searchParams.get('sort') || 'avg_daily_posts');  // Changed default
    const [sortDirection, setSortDirection] = useState(searchParams.get('direction') || 'desc');  // Changed default
    const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');

    const updateURL = (params) => {
        const newSearchParams = new URLSearchParams(location.search);
        Object.entries(params).forEach(([key, value]) => {
            if (value) {
                newSearchParams.set(key, value);
            } else {
                newSearchParams.delete(key);
            }
        });
        navigate(`${location.pathname}?${newSearchParams.toString()}`);
    };

    useEffect(() => {
        fetchWebsites();
    }, [currentPage, sortField, sortDirection, searchQuery]);

    const handleWebsiteClick = (website, newWindow = false) => {
        if (newWindow) {
            window.open(`/websites/${website.id}`, '_blank');
        } else {
            navigate(`/websites/${website.id}`);
        }
    };

    const fetchWebsites = async () => {
        setLoading(true);
        try {
            const ordering = sortDirection === 'desc' ? `-${sortField}` : sortField;
            const response = await api.get('/ready-websites/', {
                params: {
                    page: currentPage,
                    ordering: ordering,
                    search: searchQuery
                }
            });
            setWebsiteData(response.data);
        } catch (error) {
            console.error('Error fetching ready websites:', error);
            setError('Failed to load ready websites');
        } finally {
            setLoading(false);
        }
    };

    const handleSort = (field) => {
        const newDirection = field === sortField && sortDirection === 'asc' ? 'desc' : 'asc';
        setSortField(field);
        setSortDirection(newDirection);
        updateURL({ sort: field, direction: newDirection });
    };

    const handleSearch = (event) => {
        const value = event.target.value;
        setSearchQuery(value);
        setCurrentPage(1);
        updateURL({ search: value, page: '1' });
    };

    const handlePageChange = (_, page) => {
        setCurrentPage(page);
        updateURL({ page: page.toString() });
    };

    return (
        <div className="container mt-4">
            <Box mb={3}>
                <TextField
                    label="Search Ready Websites"
                    variant="outlined"
                    fullWidth
                    value={searchQuery}
                    onChange={handleSearch}
                />
            </Box>

            {loading ? (
                <Box display="flex" justifyContent="center" m={3}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Box className="alert alert-danger">{error}</Box>
            ) : (
                <>
                    <Box mb={2}>
                        <h2>Ready Websites ({websiteData?.count || 0})</h2>
                    </Box>

                    <WebsiteTable 
                        websites={websiteData} 
                        onSelectWebsite={handleWebsiteClick}
                        sortField={sortField}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                    />

                    <Box display="flex" justifyContent="center" mt={3}>
                        <Pagination
                            count={Math.ceil((websiteData?.count || 0) / 10)}
                            page={currentPage}
                            onChange={handlePageChange}
                            color="primary"
                        />
                    </Box>
                </>
            )}
        </div>
    );
};

export default ReadyWebsiteList;